import * as React from 'react';
import { useState, useEffect } from 'react';
import {
  Container,
  Paper,
  useTheme,
  useMediaQuery,
  TextField,
  Button,
  Typography,
  Box,
  CircularProgress,
  Alert,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  Tabs,
  Tab,
  Card,
  CardContent,
  CardActions,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  IconButton,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Chip,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  FormControlLabel,
  Checkbox,
  Divider,
  Breadcrumbs,
  Link,
} from '@mui/material';
import {
  AutoStories as BookIcon,
  Add as AddIcon,
  Download as DownloadIcon,
  ContentCopy as CopyIcon,
  Delete as DeleteIcon,
  History as HistoryIcon,
  ExpandMore as ExpandMoreIcon,
  Edit as EditIcon,
  Psychology as OptimizeIcon,
  SportsEsports as GameIcon,
  AccountTree as TreeIcon,
  NavigateNext as NavigateNextIcon,
} from '@mui/icons-material';
import { InteractiveFictionService } from '../services/interactiveFictionService';
import {
  InteractiveFictionSession,
  InteractiveFictionSessionStorage,
  StartInteractiveFictionSessionRequest,
  InteractiveStoryPassage,
  CreateBranchPassageRequest,
  INTERACTIVE_FICTION_GENRES,
  STORY_FORMATS,
  STORY_BEAT_TYPES,
  ENGAGEMENT_FOCUS_OPTIONS,
  GAME_MECHANICS,
} from '../models/interactiveFiction';

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;
  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`interactive-fiction-tabpanel-${index}`}
      aria-labelledby={`interactive-fiction-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ py: { xs: 2, sm: 3 } }}>{children}</Box>}
    </div>
  );
}

const InteractiveFictionAgent: React.FC = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const isTablet = useMediaQuery(theme.breakpoints.down('md'));

  const [tabValue, setTabValue] = useState(0);
  const [title, setTitle] = useState('');
  const [concept, setConcept] = useState('');
  const [genre, setGenre] = useState('');
  const [themeAndMood, setThemeAndMood] = useState('');
  const [storyFormat, setStoryFormat] = useState('Choose Your Own Adventure');
  const [lengthInPassages, setLengthInPassages] = useState(20);
  const [playerCharacterDescription, setPlayerCharacterDescription] = useState('');
  const [settingDescription, setSettingDescription] = useState('');
  const [targetAudience, setTargetAudience] = useState('');
  const [additionalInstructions, setAdditionalInstructions] = useState('');
  const [maxTokensPerTurn, setMaxTokensPerTurn] = useState(1024);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [sessionId, setSessionId] = useState<string | null>(null);
  const [currentSession, setCurrentSession] = useState<InteractiveFictionSession | null>(null);
  const [sessions, setSessions] = useState<InteractiveFictionSession[]>([]);
  const [currentPassageId, setCurrentPassageId] = useState<string | null>(null);
  const [branchDialog, setBranchDialog] = useState(false);
  const [optimizeDialog, setOptimizeDialog] = useState(false);
  const [mechanicsDialog, setMechanicsDialog] = useState(false);
  const [selectedPassageId, setSelectedPassageId] = useState<string | null>(null);
  const [visualization, setVisualization] = useState<string | null>(null);

  // Branch dialog state
  const [choiceText, setChoiceText] = useState('');
  const [branchTitle, setBranchTitle] = useState('');
  const [branchDirection, setBranchDirection] = useState('');
  const [storyBeatType, setStoryBeatType] = useState('');
  const [isEndingPassage, setIsEndingPassage] = useState(false);
  const [customContent, setCustomContent] = useState('');

  // Optimize dialog state
  const [engagementFocus, setEngagementFocus] = useState('');
  const [targetEmotions, setTargetEmotions] = useState('');
  const [optimizeInstructions, setOptimizeInstructions] = useState('');

  // Mechanics dialog state
  const [selectedMechanics, setSelectedMechanics] = useState<string[]>([]);
  const [gameStateChanges, setGameStateChanges] = useState<{ [key: string]: string }>({});

  // Load sessions from localStorage
  useEffect(() => {
    const stored = localStorage.getItem('interactiveFictionSessions');
    if (stored) {
      const data: InteractiveFictionSessionStorage = JSON.parse(stored);
      setSessions(data.sessions.map(s => ({
        ...s,
        createdAt: new Date(s.createdAt)
      })));
    }
  }, []);

  // Save sessions to localStorage
  const saveSessions = (newSessions: InteractiveFictionSession[]) => {
    const storage: InteractiveFictionSessionStorage = {
      sessions: newSessions,
      lastUpdated: new Date()
    };
    localStorage.setItem('interactiveFictionSessions', JSON.stringify(storage));
    setSessions(newSessions);
  };

  const handleStartSession = async () => {
    if (!title.trim() || !concept.trim()) {
      setError('Please provide a title and concept');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const request: StartInteractiveFictionSessionRequest = {
        title: title.trim(),
        concept: concept.trim(),
        genre: genre || undefined,
        themeAndMood: themeAndMood.trim() || undefined,
        storyFormat,
        lengthInPassages,
        playerCharacterDescription: playerCharacterDescription.trim() || undefined,
        settingDescription: settingDescription.trim() || undefined,
        targetAudience: targetAudience.trim() || undefined,
        additionalInstructions: additionalInstructions.trim() || undefined,
        maxTokensPerTurn
      };

      const response = await InteractiveFictionService.startSession(request);
      
      if (response.success && response.sessionId) {
        setSessionId(response.sessionId);
        
        const newSession: InteractiveFictionSession = {
          id: Date.now().toString(),
          sessionId: response.sessionId,
          title: title.trim(),
          concept: concept.trim(),
          genre: genre || undefined,
          passages: response.openingPassage ? [response.openingPassage] : [],
          currentPassageId: response.openingPassage?.id,
          createdAt: new Date()
        };
        
        setCurrentSession(newSession);
        setCurrentPassageId(response.openingPassage?.id || null);
        saveSessions([newSession, ...sessions]);
        setTabValue(1); // Switch to story tab
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to start interactive fiction session');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateBranch = async () => {
    if (!sessionId || !selectedPassageId || !choiceText.trim()) {
      setError('Please provide choice text');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const request: CreateBranchPassageRequest = {
        sourcePassageId: selectedPassageId,
        choiceText: choiceText.trim(),
        title: branchTitle.trim() || undefined,
        branchDirection: branchDirection || undefined,
        storyBeatType: storyBeatType || undefined,
        isEndingPassage,
        customContent: customContent.trim() || undefined,
        additionalInstructions: optimizeInstructions.trim() || undefined,
        maxTokens: maxTokensPerTurn
      };

      const response = await InteractiveFictionService.createBranch(sessionId, request);
      
      if (response.success && response.passage && currentSession) {
        const updatedSession = {
          ...currentSession,
          passages: [...currentSession.passages, response.passage]
        };
        
        setCurrentSession(updatedSession);
        setCurrentPassageId(response.passage.id || null);
        
        // Update saved sessions
        const updatedSessions = sessions.map(s => 
          s.id === currentSession.id ? updatedSession : s
        );
        saveSessions(updatedSessions);
        
        // Reset branch dialog
        setBranchDialog(false);
        setChoiceText('');
        setBranchTitle('');
        setBranchDirection('');
        setStoryBeatType('');
        setIsEndingPassage(false);
        setCustomContent('');
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to create branch');
    } finally {
      setLoading(false);
    }
  };

  const handleOptimizeEngagement = async () => {
    if (!sessionId || !selectedPassageId) return;

    setLoading(true);
    setError(null);

    try {
      const response = await InteractiveFictionService.optimizeEngagement(sessionId, {
        passageId: selectedPassageId,
        engagementFocus: engagementFocus || undefined,
        targetEmotions: targetEmotions.trim() || undefined,
        additionalInstructions: optimizeInstructions.trim() || undefined,
        maxTokens: maxTokensPerTurn
      });
      
      if (response.success && response.passage && currentSession) {
        const updatedSession = {
          ...currentSession,
          passages: currentSession.passages.map(p => 
            p.id === selectedPassageId ? response.passage! : p
          )
        };
        
        setCurrentSession(updatedSession);
        
        // Update saved sessions
        const updatedSessions = sessions.map(s => 
          s.id === currentSession.id ? updatedSession : s
        );
        saveSessions(updatedSessions);
        
        setOptimizeDialog(false);
        setEngagementFocus('');
        setTargetEmotions('');
        setOptimizeInstructions('');
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to optimize passage');
    } finally {
      setLoading(false);
    }
  };

  const handleAddMechanics = async () => {
    if (!sessionId || !selectedPassageId || selectedMechanics.length === 0) {
      setError('Please select at least one game mechanic');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const response = await InteractiveFictionService.addGameMechanics(sessionId, {
        passageId: selectedPassageId,
        mechanicsToAdd: selectedMechanics,
        gameStateChanges: Object.keys(gameStateChanges).length > 0 ? gameStateChanges : undefined,
        maxTokens: maxTokensPerTurn
      });
      
      if (response.success && response.passage && currentSession) {
        const updatedSession = {
          ...currentSession,
          passages: currentSession.passages.map(p => 
            p.id === selectedPassageId ? response.passage! : p
          )
        };
        
        setCurrentSession(updatedSession);
        
        // Update saved sessions
        const updatedSessions = sessions.map(s => 
          s.id === currentSession.id ? updatedSession : s
        );
        saveSessions(updatedSessions);
        
        setMechanicsDialog(false);
        setSelectedMechanics([]);
        setGameStateChanges({});
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to add game mechanics');
    } finally {
      setLoading(false);
    }
  };

  const handleLoadVisualization = async () => {
    if (!sessionId) return;

    setLoading(true);
    try {
      const response = await InteractiveFictionService.getVisualization(sessionId);
      if (response.success && response.visualization) {
        setVisualization(response.visualization);
      }
    } catch (err) {
      setError('Failed to load visualization');
    } finally {
      setLoading(false);
    }
  };

  const handleExportStory = () => {
    if (!currentSession) return;

    let content = `# ${currentSession.title}\n\n`;
    content += `**Genre:** ${currentSession.genre || 'Not specified'}\n\n`;
    content += `## Story Concept\n${currentSession.concept}\n\n`;
    content += `## Story Passages\n\n`;

    // Create a map to track parent-child relationships
    const passageMap = new Map<string, InteractiveStoryPassage>();
    currentSession.passages.forEach(p => {
      if (p.id) passageMap.set(p.id, p);
    });

    // Export passages in order
    currentSession.passages.forEach((passage, index) => {
      content += `### ${passage.title || `Passage ${index + 1}`}`;
      if (passage.isOpeningPassage) content += ' (Opening)';
      if (passage.isEndingPassage) content += ' (Ending)';
      content += '\n\n';
      
      content += passage.content + '\n\n';
      
      if (passage.choices && passage.choices.length > 0) {
        content += '**Choices:**\n';
        passage.choices.forEach((choice, i) => {
          content += `${i + 1}. ${choice.text}\n`;
        });
        content += '\n';
      }
      
      if (passage.gameMechanics && passage.gameMechanics.length > 0) {
        content += `**Game Mechanics:** ${passage.gameMechanics.join(', ')}\n\n`;
      }
      
      content += '---\n\n';
    });

    const blob = new Blob([content], { type: 'text/markdown' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${currentSession.title.replace(/\s+/g, '-').toLowerCase()}-interactive-fiction.md`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const handleCopyPassage = (passage: InteractiveStoryPassage) => {
    const text = `${passage.title || 'Untitled'}\n\n${passage.content}`;
    navigator.clipboard.writeText(text);
  };

  const handleLoadSession = (session: InteractiveFictionSession) => {
    setCurrentSession(session);
    setSessionId(session.sessionId || null);
    setTitle(session.title);
    setConcept(session.concept || '');
    setGenre(session.genre || '');
    setCurrentPassageId(session.currentPassageId || null);
    setTabValue(1); // Switch to story tab
  };

  const handleDeleteSession = (sessionId: string) => {
    saveSessions(sessions.filter(s => s.id !== sessionId));
  };

  const handleReset = () => {
    setTitle('');
    setConcept('');
    setGenre('');
    setThemeAndMood('');
    setStoryFormat('Choose Your Own Adventure');
    setLengthInPassages(20);
    setPlayerCharacterDescription('');
    setSettingDescription('');
    setTargetAudience('');
    setAdditionalInstructions('');
    setMaxTokensPerTurn(1024);
    setSessionId(null);
    setCurrentSession(null);
    setCurrentPassageId(null);
    setVisualization(null);
    setTabValue(0);
  };

  const getCurrentPassage = () => {
    if (!currentSession || !currentPassageId) return null;
    return currentSession.passages.find(p => p.id === currentPassageId);
  };

  const getPassagePath = (passageId: string): InteractiveStoryPassage[] => {
    if (!currentSession) return [];
    
    const path: InteractiveStoryPassage[] = [];
    let currentId: string | null = passageId;
    
    while (currentId) {
      const passage = currentSession.passages.find(p => p.id === currentId);
      if (!passage) break;
      path.unshift(passage);
      currentId = passage.parentPassageId || null;
    }
    
    return path;
  };

  return (
    <Container maxWidth="md" sx={{ px: { xs: 1, sm: 2, md: 3 }, py: { xs: 2, sm: 3, md: 4 } }}>
      {/* Header */}
      <Box sx={{ textAlign: 'center', mb: { xs: 2, sm: 3, md: 4 } }}>
        <Typography 
          variant="h4" 
          sx={{ 
            display: 'flex', 
            alignItems: 'center', 
            justifyContent: 'center', 
            gap: { xs: 1, sm: 1.5 },
            flexWrap: 'wrap',
            mb: { xs: 1, sm: 2 },
            fontSize: { xs: '1.5rem', sm: '1.875rem', md: '2.125rem' }
          }}
        >
          <BookIcon sx={{ fontSize: { xs: 32, sm: 40 } }} />
          {isMobile ? 'Fiction Creator' : 'Interactive Fiction Creator'}
        </Typography>
        <Typography 
          variant="body1" 
          color="text.secondary"
          sx={{ fontSize: { xs: '0.875rem', sm: '1rem' } }}
        >
          {isMobile ? 'Create branching AI stories' : 'Create branching narratives and interactive stories with AI assistance'}
        </Typography>
      </Box>

      {/* Tab Navigation */}
      <Paper sx={{ mb: { xs: 2, sm: 3 } }}>
        <Tabs 
          value={tabValue} 
          onChange={(_, newValue) => setTabValue(newValue)}
          variant={isMobile ? 'scrollable' : 'fullWidth'}
          scrollButtons={isMobile ? 'auto' : false}
          sx={{ 
            borderBottom: 1, 
            borderColor: 'divider',
            '& .MuiTab-root': {
              fontSize: { xs: '0.8125rem', sm: '0.875rem' },
              minHeight: 48
            }
          }}
        >
          <Tab label={isMobile ? 'Create' : 'Create Story'} sx={{ textTransform: 'none' }} />
          <Tab label={isMobile ? 'Editor' : 'Story Editor'} sx={{ textTransform: 'none' }} disabled={!currentSession} />
          <Tab label={isMobile ? 'Map' : 'Visualization'} sx={{ textTransform: 'none' }} disabled={!sessionId} />
          <Tab label="History" sx={{ textTransform: 'none' }} />
        </Tabs>
      </Paper>

        <TabPanel value={tabValue} index={0}>
          <Paper sx={{ p: { xs: 2, sm: 3, md: 4 } }}>
            <Typography 
              variant="h5" 
              gutterBottom 
              sx={{ 
                mb: { xs: 2, sm: 3 },
                fontSize: { xs: '1.25rem', sm: '1.5rem' }
              }}
            >
              {isMobile ? 'Config' : 'Story Configuration'}
            </Typography>
            
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: { xs: 2, sm: 3 } }}>
              {/* Basic Information */}
              <TextField
                fullWidth
                label={isMobile ? 'Title' : 'Story Title'}
                placeholder={isMobile ? 'Story title...' : 'e.g., The Enchanted Forest Adventure'}
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                required
                variant="outlined"
                InputProps={{
                  sx: { fontSize: { xs: '0.875rem', sm: '1rem' } }
                }}
                InputLabelProps={{
                  sx: { fontSize: { xs: '0.875rem', sm: '1rem' } }
                }}
              />

              <TextField
                fullWidth
                multiline
                rows={isMobile ? 3 : 4}
                label={isMobile ? 'Concept' : 'Story Concept'}
                placeholder={isMobile ? 'Story concept...' : 'Describe your interactive story concept...'}
                value={concept}
                onChange={(e) => setConcept(e.target.value)}
                required
                variant="outlined"
                InputProps={{
                  sx: { fontSize: { xs: '0.875rem', sm: '1rem' } }
                }}
                InputLabelProps={{
                  sx: { fontSize: { xs: '0.875rem', sm: '1rem' } }
                }}
              />

              {/* Genre and Format */}
              <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 2 }}>
                <FormControl fullWidth>
                  <InputLabel sx={{ fontSize: { xs: '0.875rem', sm: '1rem' } }}>Genre</InputLabel>
                  <Select
                    value={genre}
                    onChange={(e) => setGenre(e.target.value)}
                    label="Genre"
                    sx={{ fontSize: { xs: '0.875rem', sm: '1rem' } }}
                  >
                    <MenuItem value="" sx={{ fontSize: { xs: '0.875rem', sm: '1rem' } }}>None</MenuItem>
                    {INTERACTIVE_FICTION_GENRES.map((g) => (
                      <MenuItem key={g} value={g} sx={{ fontSize: { xs: '0.875rem', sm: '1rem' } }}>
                        {g}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>

                <FormControl fullWidth>
                  <InputLabel sx={{ fontSize: { xs: '0.875rem', sm: '1rem' } }}>{isMobile ? 'Format' : 'Story Format'}</InputLabel>
                  <Select
                    value={storyFormat}
                    onChange={(e) => setStoryFormat(e.target.value)}
                    label={isMobile ? 'Format' : 'Story Format'}
                    sx={{ fontSize: { xs: '0.875rem', sm: '1rem' } }}
                  >
                    {STORY_FORMATS.map((f) => (
                      <MenuItem key={f} value={f} sx={{ fontSize: { xs: '0.875rem', sm: '1rem' } }}>
                        {f}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Box>

              {/* Theme and Setting */}
              <TextField
                fullWidth
                label={isMobile ? 'Theme/Mood' : 'Theme and Mood'}
                placeholder={isMobile ? 'Dark and mysterious...' : 'e.g., Dark and mysterious with elements of hope'}
                value={themeAndMood}
                onChange={(e) => setThemeAndMood(e.target.value)}
                variant="outlined"
                InputProps={{
                  sx: { fontSize: { xs: '0.875rem', sm: '1rem' } }
                }}
                InputLabelProps={{
                  sx: { fontSize: { xs: '0.875rem', sm: '1rem' } }
                }}
              />

              <TextField
                fullWidth
                multiline
                rows={isMobile ? 2 : 3}
                label={isMobile ? 'Player Character' : 'Player Character Description'}
                placeholder={isMobile ? 'Main character...' : 'Describe the main character...'}
                value={playerCharacterDescription}
                onChange={(e) => setPlayerCharacterDescription(e.target.value)}
                variant="outlined"
                InputProps={{
                  sx: { fontSize: { xs: '0.875rem', sm: '1rem' } }
                }}
                InputLabelProps={{
                  sx: { fontSize: { xs: '0.875rem', sm: '1rem' } }
                }}
              />

              <TextField
                fullWidth
                multiline
                rows={isMobile ? 2 : 3}
                label={isMobile ? 'Setting' : 'Setting Description'}
                placeholder={isMobile ? 'World/setting...' : 'Describe the world/setting...'}
                value={settingDescription}
                onChange={(e) => setSettingDescription(e.target.value)}
                variant="outlined"
                InputProps={{
                  sx: { fontSize: { xs: '0.875rem', sm: '1rem' } }
                }}
                InputLabelProps={{
                  sx: { fontSize: { xs: '0.875rem', sm: '1rem' } }
                }}
              />

              {/* Technical Settings */}
              <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '2fr 1fr 1fr' }, gap: 2 }}>
                <TextField
                  fullWidth
                  label={isMobile ? 'Audience' : 'Target Audience'}
                  placeholder={isMobile ? 'e.g., Young Adults' : 'e.g., Young Adults, Fantasy Fans'}
                  value={targetAudience}
                  onChange={(e) => setTargetAudience(e.target.value)}
                  variant="outlined"
                  InputProps={{
                    sx: { fontSize: { xs: '0.875rem', sm: '1rem' } }
                  }}
                  InputLabelProps={{
                    sx: { fontSize: { xs: '0.875rem', sm: '1rem' } }
                  }}
                />

                <TextField
                  type="number"
                  label={isMobile ? 'Length' : 'Length (passages)'}
                  value={lengthInPassages}
                  onChange={(e) => setLengthInPassages(parseInt(e.target.value) || 20)}
                  inputProps={{ min: 5, max: 100 }}
                  variant="outlined"
                  InputProps={{
                    sx: { fontSize: { xs: '0.875rem', sm: '1rem' } }
                  }}
                  InputLabelProps={{
                    sx: { fontSize: { xs: '0.875rem', sm: '1rem' } }
                  }}
                />

                <TextField
                  type="number"
                  label={isMobile ? 'Tokens' : 'Max Tokens'}
                  value={maxTokensPerTurn}
                  onChange={(e) => setMaxTokensPerTurn(parseInt(e.target.value) || 1024)}
                  inputProps={{ min: 512, max: 4096 }}
                  variant="outlined"
                  InputProps={{
                    sx: { fontSize: { xs: '0.875rem', sm: '1rem' } }
                  }}
                  InputLabelProps={{
                    sx: { fontSize: { xs: '0.875rem', sm: '1rem' } }
                  }}
                />
              </Box>

              {/* Additional Instructions */}
              <TextField
                fullWidth
                multiline
                rows={isMobile ? 2 : 3}
                label={isMobile ? 'Instructions' : 'Additional Instructions'}
                placeholder={isMobile ? 'Requirements...' : 'Any specific requirements or style preferences...'}
                value={additionalInstructions}
                onChange={(e) => setAdditionalInstructions(e.target.value)}
                variant="outlined"
                InputProps={{
                  sx: { fontSize: { xs: '0.875rem', sm: '1rem' } }
                }}
                InputLabelProps={{
                  sx: { fontSize: { xs: '0.875rem', sm: '1rem' } }
                }}
              />

              {error && (
                <Alert 
                  severity="error" 
                  onClose={() => setError(null)}
                  sx={{ fontSize: { xs: '0.8125rem', sm: '0.875rem' } }}
                >
                  {error}
                </Alert>
              )}

              {/* Action Button */}
              <Button
                fullWidth
                variant="contained"
                size={isMobile ? 'medium' : 'large'}
                onClick={handleStartSession}
                disabled={loading || !title.trim() || !concept.trim()}
                startIcon={loading ? <CircularProgress size={20} /> : (!isMobile && <BookIcon />)}
                sx={{ 
                  mt: { xs: 1.5, sm: 2 },
                  minHeight: 44,
                  fontSize: { xs: '0.875rem', sm: '0.9375rem' }
                }}
              >
                {loading ? (isMobile ? 'Creating...' : 'Creating Interactive Story...') : (isMobile ? 'Start Story' : 'Start Interactive Story')}
              </Button>
            </Box>
          </Paper>
        </TabPanel>

        <TabPanel value={tabValue} index={1}>
          {currentSession ? (
            <Paper sx={{ p: { xs: 2, sm: 3, md: 4 } }}>
              {currentPassageId && (
                <Breadcrumbs 
                  separator={<NavigateNextIcon fontSize="small" />}
                  sx={{ mb: { xs: 2, sm: 3 } }}
                >
                  {getPassagePath(currentPassageId).map((passage, index) => (
                    <Link
                      key={passage.id}
                      component="button"
                      variant="body2"
                      onClick={() => setCurrentPassageId(passage.id || null)}
                      underline="hover"
                      color={passage.id === currentPassageId ? 'text.primary' : 'inherit'}
                      sx={{ fontSize: { xs: '0.8125rem', sm: '0.875rem' } }}
                    >
                      {isMobile && passage.title 
                        ? passage.title.length > 15 
                          ? `${passage.title.substring(0, 12)}...` 
                          : passage.title
                        : (passage.title || `Passage ${index + 1}`)}
                    </Link>
                  ))}
                </Breadcrumbs>
              )}

              <Box sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' }, gap: { xs: 2, sm: 3 }, minHeight: { xs: 'auto', sm: '500px' } }}>
                {/* Passage Tree */}
                <Box sx={{ 
                  width: { xs: '100%', md: '35%' }, 
                  maxHeight: { xs: '300px', md: 'auto' },
                  overflowY: 'auto',
                  borderRight: { xs: 0, md: 1 },
                  borderBottom: { xs: 1, md: 0 },
                  borderColor: 'divider',
                  pr: { xs: 0, md: 2 },
                  pb: { xs: 2, md: 0 }
                }}>
                  <Typography 
                    variant="h6" 
                    gutterBottom 
                    sx={{ 
                      mb: { xs: 1.5, sm: 2 },
                      fontSize: { xs: '1rem', sm: '1.125rem' }
                    }}
                  >
                    {isMobile ? 'Structure' : 'Story Structure'}
                  </Typography>
                  <List sx={{ bgcolor: 'background.paper' }}>
                    {currentSession.passages.map((passage, index) => (
                      <ListItem
                        key={passage.id}
                        button
                        selected={passage.id === currentPassageId}
                        onClick={() => setCurrentPassageId(passage.id || null)}
                        sx={{ 
                          pl: passage.parentPassageId ? (isMobile ? 3 : 4) : (isMobile ? 1.5 : 2),
                          py: { xs: 1, sm: 1.5 }
                        }}
                      >
                        <ListItemText
                          primary={passage.title || `Passage ${index + 1}`}
                          primaryTypographyProps={{
                            sx: { fontSize: { xs: '0.875rem', sm: '1rem' } }
                          }}
                          secondary={
                            <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap', mt: 0.5 }}>
                              {passage.isOpeningPassage && <Chip label={isMobile ? 'Open' : 'Opening'} size="small" sx={{ fontSize: { xs: '0.65rem', sm: '0.7rem' } }} />}
                              {passage.isEndingPassage && <Chip label={isMobile ? 'End' : 'Ending'} size="small" sx={{ fontSize: { xs: '0.65rem', sm: '0.7rem' } }} />}
                              {passage.gameMechanics && passage.gameMechanics.length > 0 && (
                                <Chip label={`${passage.gameMechanics.length} ${isMobile ? 'mech' : 'mechanics'}`} size="small" sx={{ fontSize: { xs: '0.65rem', sm: '0.7rem' } }} />
                              )}
                            </Box>
                          }
                        />
                      </ListItem>
                    ))}
                  </List>
                </Box>

                {/* Current Passage */}
                <Box sx={{ flex: 1, overflowY: 'auto', pl: { xs: 0, md: 2 } }}>
                  {getCurrentPassage() ? (
                    <Card>
                      <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: { xs: 1.5, sm: 2 }, flexWrap: 'wrap', gap: 1 }}>
                          <Typography 
                            variant="h5" 
                            component="h2"
                            sx={{ fontSize: { xs: '1.125rem', sm: '1.5rem' } }}
                          >
                            {getCurrentPassage()?.title || 'Untitled Passage'}
                          </Typography>
                          <Box>
                            <IconButton 
                              onClick={() => handleCopyPassage(getCurrentPassage()!)}
                              size="small"
                              sx={{ minWidth: 44, minHeight: 44 }}
                              title={isMobile ? 'Copy' : 'Copy passage'}
                            >
                              <CopyIcon />
                            </IconButton>
                            <IconButton
                              onClick={() => {
                                setSelectedPassageId(currentPassageId);
                                setOptimizeDialog(true);
                              }}
                              size="small"
                              color="primary"
                              sx={{ minWidth: 44, minHeight: 44 }}
                              title={isMobile ? 'Optimize' : 'Optimize passage'}
                            >
                              <OptimizeIcon />
                            </IconButton>
                            <IconButton
                              onClick={() => {
                                setSelectedPassageId(currentPassageId);
                                setMechanicsDialog(true);
                              }}
                              size="small"
                              color="secondary"
                              sx={{ minWidth: 44, minHeight: 44 }}
                              title={isMobile ? 'Mechanics' : 'Game mechanics'}
                            >
                              <GameIcon />
                            </IconButton>
                          </Box>
                        </Box>

                        <Typography 
                          paragraph 
                          sx={{ 
                            whiteSpace: 'pre-wrap',
                            fontSize: { xs: '0.9375rem', sm: '1rem', md: '1.1rem' },
                            lineHeight: { xs: 1.6, sm: 1.8 }
                          }}
                        >
                          {getCurrentPassage()?.content}
                        </Typography>

                        {getCurrentPassage()?.gameMechanics && getCurrentPassage()!.gameMechanics!.length > 0 && (
                          <Box sx={{ mt: { xs: 1.5, sm: 2 } }}>
                            <Typography 
                              variant="subtitle2" 
                              gutterBottom
                              sx={{ fontSize: { xs: '0.8125rem', sm: '0.875rem' } }}
                            >
                              {isMobile ? 'Mechanics:' : 'Game Mechanics:'}
                            </Typography>
                            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: { xs: 0.5, sm: 1 } }}>
                              {getCurrentPassage()!.gameMechanics!.map((mechanic, i) => (
                                <Chip 
                                  key={i} 
                                  label={mechanic} 
                                  size="small" 
                                  color="secondary" 
                                  sx={{ fontSize: { xs: '0.7rem', sm: '0.75rem' } }}
                                />
                              ))}
                            </Box>
                          </Box>
                        )}

                        {getCurrentPassage()?.choices && getCurrentPassage()!.choices!.length > 0 && (
                          <Box sx={{ mt: { xs: 2, sm: 3 } }}>
                            <Typography 
                              variant="h6" 
                              gutterBottom
                              sx={{ fontSize: { xs: '1rem', sm: '1.125rem' } }}
                            >
                              Choices:
                            </Typography>
                            {getCurrentPassage()!.choices!.map((choice, index) => (
                              <Card key={choice.id} variant="outlined" sx={{ mb: 1 }}>
                                <CardContent sx={{ p: { xs: 1.5, sm: 2 }, '&:last-child': { pb: { xs: 1.5, sm: 2 } } }}>
                                  <Typography sx={{ fontSize: { xs: '0.875rem', sm: '1rem' } }}>
                                    {index + 1}. {choice.text}
                                  </Typography>
                                  {choice.condition && (
                                    <Typography 
                                      variant="caption" 
                                      color="text.secondary"
                                      sx={{ fontSize: { xs: '0.7rem', sm: '0.75rem' } }}
                                    >
                                      Condition: {choice.condition}
                                    </Typography>
                                  )}
                                </CardContent>
                              </Card>
                            ))}
                          </Box>
                        )}
                      </CardContent>
                      <CardActions>
                        {!getCurrentPassage()?.isEndingPassage && (
                          <Button
                            startIcon={<AddIcon />}
                            onClick={() => {
                              setSelectedPassageId(currentPassageId);
                              setBranchDialog(true);
                            }}
                          >
                            Add Branch
                          </Button>
                        )}
                      </CardActions>
                    </Card>
                  ) : (
                    <Typography color="text.secondary" sx={{ textAlign: 'center', mt: 4 }}>
                      Select a passage from the story structure to view and edit
                    </Typography>
                  )}
                </Box>
              </Box>
            </Paper>
          ) : (
            <Paper sx={{ p: 6, textAlign: 'center' }}>
              <BookIcon sx={{ fontSize: 60, color: 'text.secondary', mb: 2 }} />
              <Typography variant="h6" color="text.secondary" gutterBottom>
                No Story Session Active
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                Create a new story or load an existing one to start editing
              </Typography>
              <Button
                variant="outlined"
                onClick={() => setTabValue(0)}
                startIcon={<BookIcon />}
              >
                Create New Story
              </Button>
            </Paper>
          )}
        </TabPanel>

        <TabPanel value={tabValue} index={2}>
          <Paper sx={{ p: { xs: 2, sm: 3, md: 4 } }}>
            <Typography 
              variant="h5" 
              gutterBottom 
              sx={{ 
                mb: { xs: 2, sm: 3 }, 
                textAlign: 'center',
                fontSize: { xs: '1.25rem', sm: '1.5rem' }
              }}
            >
              {isMobile ? 'Visualization' : 'Story Visualization'}
            </Typography>
            <Box sx={{ textAlign: 'center' }}>
              <Button 
                variant="contained" 
                size={isMobile ? 'medium' : 'large'}
                onClick={handleLoadVisualization}
                disabled={loading}
                startIcon={loading ? <CircularProgress size={20} /> : (!isMobile && <TreeIcon />)}
                sx={{ 
                  minHeight: 44,
                  fontSize: { xs: '0.875rem', sm: '0.9375rem' }
                }}
              >
                {loading ? (isMobile ? 'Generating...' : 'Generating Visualization...') : (isMobile ? 'Generate Map' : 'Generate Story Visualization')}
              </Button>
              
              {visualization && (
                <Box 
                  sx={{ 
                    mt: { xs: 2, sm: 3 }, 
                    p: { xs: 2, sm: 3 }, 
                    backgroundColor: (theme) => theme.palette.mode === 'light' ? 'background.default' : 'background.paper',
                    border: (theme) => `1px solid ${theme.palette.divider}`,
                    borderRadius: 2,
                    fontFamily: 'monospace',
                    fontSize: { xs: '0.75rem', sm: '0.875rem' },
                    whiteSpace: 'pre-wrap',
                    overflow: 'auto',
                    maxHeight: { xs: '50vh', sm: '60vh' },
                    textAlign: 'left'
                  }}
                >
                  {visualization}
                </Box>
              )}
            </Box>
          </Paper>
        </TabPanel>

        <TabPanel value={tabValue} index={3}>
          {sessions.length > 0 ? (
            <Paper sx={{ p: { xs: 2, sm: 3, md: 4 } }}>
              <Typography 
                variant="h5" 
                gutterBottom 
                sx={{ 
                  mb: { xs: 2, sm: 3 },
                  fontSize: { xs: '1.25rem', sm: '1.5rem' }
                }}
              >
                {isMobile ? `History (${sessions.length})` : `Story History (${sessions.length})`}
              </Typography>
              <List sx={{ bgcolor: 'background.paper' }}>
                {sessions.map((session) => (
                  <ListItem 
                    key={session.id} 
                    divider
                    sx={{ 
                      cursor: 'pointer',
                      borderRadius: 1,
                      mb: 1,
                      py: { xs: 1.5, sm: 2 },
                      '&:hover': { 
                        backgroundColor: 'action.hover',
                        borderRadius: 1
                      }
                    }}
                    onClick={() => handleLoadSession(session)}
                  >
                    <ListItemText
                      primary={
                        <Typography 
                          variant="h6" 
                          sx={{ 
                            mb: 0.5,
                            fontSize: { xs: '1rem', sm: '1.125rem' }
                          }}
                        >
                          {session.title}
                        </Typography>
                      }
                      secondary={
                        <Box>
                          <Typography 
                            variant="body2" 
                            color="text.secondary"
                            sx={{ fontSize: { xs: '0.8125rem', sm: '0.875rem' } }}
                          >
                            {session.genre && `${session.genre} • `}
                            {session.passages.length} {isMobile ? 'pass.' : 'passages'}
                          </Typography>
                          <Typography 
                            variant="caption" 
                            color="text.secondary"
                            sx={{ fontSize: { xs: '0.7rem', sm: '0.75rem' } }}
                          >
                            {isMobile ? '' : 'Created: '}{session.createdAt.toLocaleDateString()}
                          </Typography>
                        </Box>
                      }
                    />
                    <ListItemSecondaryAction>
                      <Tooltip title={isMobile ? 'Export' : 'Export'}>
                        <IconButton 
                          edge="end" 
                          onClick={(e) => {
                            e.stopPropagation();
                            setCurrentSession(session);
                            handleExportStory();
                          }}
                          sx={{ mr: 1, minWidth: 44, minHeight: 44 }}
                        >
                          <DownloadIcon />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title={isMobile ? 'Del' : 'Delete'}>
                        <IconButton 
                          edge="end" 
                          onClick={(e) => {
                            e.stopPropagation();
                            if (window.confirm('Delete this story?')) {
                              handleDeleteSession(session.id);
                            }
                          }}
                          sx={{ minWidth: 44, minHeight: 44 }}
                        >
                          <DeleteIcon />
                        </IconButton>
                      </Tooltip>
                    </ListItemSecondaryAction>
                  </ListItem>
                ))}
              </List>
            </Paper>
          ) : (
            <Paper sx={{ p: { xs: 4, sm: 6 }, textAlign: 'center' }}>
              <BookIcon sx={{ fontSize: { xs: 48, sm: 60 }, color: 'text.secondary', mb: { xs: 1.5, sm: 2 } }} />
              <Typography 
                variant="h6" 
                color="text.secondary" 
                gutterBottom
                sx={{ fontSize: { xs: '1rem', sm: '1.125rem' } }}
              >
                {isMobile ? 'No stories yet' : 'No interactive stories created yet'}
              </Typography>
              <Typography 
                variant="body2" 
                color="text.secondary" 
                sx={{ 
                  mb: { xs: 2, sm: 3 },
                  fontSize: { xs: '0.875rem', sm: '1rem' }
                }}
              >
                {isMobile ? 'Stories appear here' : 'Your interactive fiction projects will appear here for easy access'}
              </Typography>
              <Button
                variant="outlined"
                onClick={() => setTabValue(0)}
                startIcon={!isMobile && <BookIcon />}
                sx={{ 
                  minHeight: 44,
                  fontSize: { xs: '0.875rem', sm: '0.9375rem' }
                }}
              >
                {isMobile ? 'Create Story' : 'Create Your First Story'}
              </Button>
            </Paper>
          )}
        </TabPanel>

      {/* Branch Dialog */}
      <Dialog 
        open={branchDialog} 
        onClose={() => setBranchDialog(false)} 
        maxWidth="sm" 
        fullWidth
        fullScreen={isMobile}
      >
        <DialogTitle sx={{ fontSize: { xs: '1.125rem', sm: '1.25rem' } }}>
          {isMobile ? 'Create Branch' : 'Create Story Branch'}
        </DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: { xs: 1.5, sm: 2 }, pt: 1 }}>
            <TextField
              fullWidth
              label={isMobile ? 'Choice' : 'Choice Text'}
              placeholder={isMobile ? 'Reader will click...' : 'What the reader will click...'}
              value={choiceText}
              onChange={(e) => setChoiceText(e.target.value)}
              autoFocus
              required
              InputProps={{
                sx: { fontSize: { xs: '0.875rem', sm: '1rem' } }
              }}
              InputLabelProps={{
                sx: { fontSize: { xs: '0.875rem', sm: '1rem' } }
              }}
            />
            <TextField
              fullWidth
              label={isMobile ? 'Title' : 'Branch Title'}
              placeholder={isMobile ? 'Passage title' : 'Title for the new passage'}
              value={branchTitle}
              onChange={(e) => setBranchTitle(e.target.value)}
              InputProps={{
                sx: { fontSize: { xs: '0.875rem', sm: '1rem' } }
              }}
              InputLabelProps={{
                sx: { fontSize: { xs: '0.875rem', sm: '1rem' } }
              }}
            />
            <TextField
              fullWidth
              label={isMobile ? 'Direction' : 'Branch Direction'}
              placeholder={isMobile ? 'e.g., Take left path' : "e.g., 'Take the left path', 'Confront the villain'"}
              value={branchDirection}
              onChange={(e) => setBranchDirection(e.target.value)}
              InputProps={{
                sx: { fontSize: { xs: '0.875rem', sm: '1rem' } }
              }}
              InputLabelProps={{
                sx: { fontSize: { xs: '0.875rem', sm: '1rem' } }
              }}
            />
            <FormControl fullWidth>
              <InputLabel sx={{ fontSize: { xs: '0.875rem', sm: '1rem' } }}>
                {isMobile ? 'Beat Type' : 'Story Beat Type'}
              </InputLabel>
              <Select
                value={storyBeatType}
                onChange={(e) => setStoryBeatType(e.target.value)}
                label={isMobile ? 'Beat Type' : 'Story Beat Type'}
                sx={{ fontSize: { xs: '0.875rem', sm: '1rem' } }}
              >
                <MenuItem value="" sx={{ fontSize: { xs: '0.875rem', sm: '1rem' } }}>None</MenuItem>
                {STORY_BEAT_TYPES.map((type) => (
                  <MenuItem key={type} value={type} sx={{ fontSize: { xs: '0.875rem', sm: '1rem' } }}>
                    {type}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <FormControlLabel
              control={
                <Checkbox
                  checked={isEndingPassage}
                  onChange={(e) => setIsEndingPassage(e.target.checked)}
                />
              }
              label={isMobile ? 'Ending passage' : 'This is an ending passage'}
              sx={{
                '& .MuiFormControlLabel-label': {
                  fontSize: { xs: '0.875rem', sm: '1rem' }
                }
              }}
            />
            <TextField
              fullWidth
              multiline
              rows={isMobile ? 2 : 3}
              label={isMobile ? 'Custom' : 'Custom Content (Optional)'}
              placeholder={isMobile ? 'Specific content...' : 'Specific content for this passage...'}
              value={customContent}
              onChange={(e) => setCustomContent(e.target.value)}
              InputProps={{
                sx: { fontSize: { xs: '0.875rem', sm: '1rem' } }
              }}
              InputLabelProps={{
                sx: { fontSize: { xs: '0.875rem', sm: '1rem' } }
              }}
            />
          </Box>
        </DialogContent>
        <DialogActions sx={{ p: { xs: 2, sm: 2 } }}>
          <Button 
            onClick={() => setBranchDialog(false)}
            sx={{ 
              minHeight: 44,
              fontSize: { xs: '0.875rem', sm: '0.9375rem' }
            }}
          >
            Cancel
          </Button>
          <Button 
            onClick={handleCreateBranch} 
            variant="contained" 
            disabled={!choiceText.trim() || loading}
            startIcon={loading && <CircularProgress size={20} />}
            sx={{ 
              minHeight: 44,
              fontSize: { xs: '0.875rem', sm: '0.9375rem' }
            }}
          >
            {isMobile ? 'Create' : 'Create Branch'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Optimize Dialog */}
      <Dialog 
        open={optimizeDialog} 
        onClose={() => setOptimizeDialog(false)} 
        maxWidth="sm" 
        fullWidth
        fullScreen={isMobile}
      >
        <DialogTitle sx={{ fontSize: { xs: '1.125rem', sm: '1.25rem' } }}>
          {isMobile ? 'Optimize Passage' : 'Optimize Passage for Engagement'}
        </DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: { xs: 1.5, sm: 2 }, pt: 1 }}>
            <FormControl fullWidth>
              <InputLabel sx={{ fontSize: { xs: '0.875rem', sm: '1rem' } }}>
                {isMobile ? 'Focus' : 'Engagement Focus'}
              </InputLabel>
              <Select
                value={engagementFocus}
                onChange={(e) => setEngagementFocus(e.target.value)}
                label={isMobile ? 'Focus' : 'Engagement Focus'}
                sx={{ fontSize: { xs: '0.875rem', sm: '1rem' } }}
              >
                <MenuItem value="" sx={{ fontSize: { xs: '0.875rem', sm: '1rem' } }}>None</MenuItem>
                {ENGAGEMENT_FOCUS_OPTIONS.map((option) => (
                  <MenuItem key={option} value={option} sx={{ fontSize: { xs: '0.875rem', sm: '1rem' } }}>
                    {option}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <TextField
              fullWidth
              label={isMobile ? 'Emotions' : 'Target Emotions'}
              placeholder="e.g., excitement, curiosity, fear"
              value={targetEmotions}
              onChange={(e) => setTargetEmotions(e.target.value)}
              InputProps={{
                sx: { fontSize: { xs: '0.875rem', sm: '1rem' } }
              }}
              InputLabelProps={{
                sx: { fontSize: { xs: '0.875rem', sm: '1rem' } }
              }}
            />
            <TextField
              fullWidth
              multiline
              rows={isMobile ? 2 : 3}
              label={isMobile ? 'Instructions' : 'Additional Instructions'}
              placeholder={isMobile ? 'Goals...' : 'Specific optimization goals...'}
              value={optimizeInstructions}
              onChange={(e) => setOptimizeInstructions(e.target.value)}
              InputProps={{
                sx: { fontSize: { xs: '0.875rem', sm: '1rem' } }
              }}
              InputLabelProps={{
                sx: { fontSize: { xs: '0.875rem', sm: '1rem' } }
              }}
            />
          </Box>
        </DialogContent>
        <DialogActions sx={{ p: { xs: 2, sm: 2 } }}>
          <Button 
            onClick={() => setOptimizeDialog(false)}
            sx={{ 
              minHeight: 44,
              fontSize: { xs: '0.875rem', sm: '0.9375rem' }
            }}
          >
            Cancel
          </Button>
          <Button 
            onClick={handleOptimizeEngagement} 
            variant="contained" 
            disabled={loading}
            startIcon={loading && <CircularProgress size={20} />}
            sx={{ 
              minHeight: 44,
              fontSize: { xs: '0.875rem', sm: '0.9375rem' }
            }}
          >
            {isMobile ? 'Optimize' : 'Optimize Passage'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Mechanics Dialog */}
      <Dialog 
        open={mechanicsDialog} 
        onClose={() => setMechanicsDialog(false)} 
        maxWidth="sm" 
        fullWidth
        fullScreen={isMobile}
      >
        <DialogTitle sx={{ fontSize: { xs: '1.125rem', sm: '1.25rem' } }}>
          {isMobile ? 'Add Mechanics' : 'Add Game Mechanics'}
        </DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: { xs: 1.5, sm: 2 }, pt: 1 }}>
            <Typography 
              variant="subtitle2"
              sx={{ fontSize: { xs: '0.8125rem', sm: '0.875rem' } }}
            >
              {isMobile ? 'Select Mechanics:' : 'Select Mechanics to Add:'}
            </Typography>
            {GAME_MECHANICS.map((mechanic) => (
              <FormControlLabel
                key={mechanic}
                control={
                  <Checkbox
                    checked={selectedMechanics.includes(mechanic)}
                    onChange={(e) => {
                      if (e.target.checked) {
                        setSelectedMechanics([...selectedMechanics, mechanic]);
                      } else {
                        setSelectedMechanics(selectedMechanics.filter(m => m !== mechanic));
                      }
                    }}
                  />
                }
                label={mechanic}
                sx={{
                  '& .MuiFormControlLabel-label': {
                    fontSize: { xs: '0.875rem', sm: '1rem' }
                  }
                }}
              />
            ))}
            <Divider />
            <Typography 
              variant="subtitle2"
              sx={{ fontSize: { xs: '0.8125rem', sm: '0.875rem' } }}
            >
              {isMobile ? 'State Changes:' : 'Game State Changes (Optional):'}
            </Typography>
            <Button 
              size="small" 
              onClick={() => {
                const key = prompt('State variable name:');
                if (key) {
                  const value = prompt('State value:');
                  if (value) {
                    setGameStateChanges({ ...gameStateChanges, [key]: value });
                  }
                }
              }}
            >
              Add State Change
            </Button>
            {Object.entries(gameStateChanges).map(([key, value]) => (
              <Chip
                key={key}
                label={`${key}: ${value}`}
                onDelete={() => {
                  const newState = { ...gameStateChanges };
                  delete newState[key];
                  setGameStateChanges(newState);
                }}
              />
            ))}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setMechanicsDialog(false)}>Cancel</Button>
          <Button 
            onClick={handleAddMechanics} 
            variant="contained" 
            disabled={selectedMechanics.length === 0 || loading}
            startIcon={loading && <CircularProgress size={20} />}
          >
            Add Mechanics
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default InteractiveFictionAgent;